import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Building2, CalendarDays, Wallet, FileSignature, CheckCircle2 } from "lucide-react";

export default function ValoresEmpresas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState(null);
  const [labels, setLabels] = useState({});
  const isMontador = user?.role === "montador";
  const rotaContrato = isMontador ? "/montador/condicoes" : "/montadora/condicoes-empresas";

  const carregar = () => {
    api.get("/me/valores-empresas")
      .then(r => setEmpresas(r.data))
      .catch(e => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    carregar();
    api.get("/tipos-servico-padrao")
      .then(r => setLabels(Object.fromEntries(r.data.map(t => [t.key, t.label]))))
      .catch(() => {});
    // eslint-disable-next-line
  }, []);

  const recusar = async (e) => {
    if (!window.confirm(`Recusar as condições de ${e.empresa_nome}? Você não receberá serviços desta empresa e ela sairá desta lista.`)) return;
    try {
      await api.post(`/condicoes-aceite/${e.aceite.id}/responder`, { acao: "recusar" });
      toast.success(`Condições de ${e.empresa_nome} recusadas.`);
      setEmpresas(prev => prev.filter(x => x.empresa_id !== e.empresa_id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const fmtValor = (it) => {
    const v = isMontador ? (it.valor_montador ?? it.valor ?? 0) : (it.valor_montadora ?? 0);
    return it.modo === "percentual" ? `${v}% do produto` : `R$ ${Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="space-y-6 p-1" data-testid="valores-empresas-page">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Financeiro</div>
        <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1">Valores das Empresas</h1>
        <p className="text-sm text-aj-text-muted mt-1 max-w-2xl">
          Quanto cada empresa paga {isMontador ? "ao montador" : "à montadora"} por tipo de serviço e as datas de pagamento.
          Cada empresa define suas próprias condições.
        </p>
      </div>

      {empresas?.length === 0 && (
        <div className="aj-card p-10 text-center text-aj-text-muted" data-testid="ve-vazio">
          Nenhuma empresa cadastrou tabela de valores ainda.
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {empresas?.map((e) => (
          <div key={e.empresa_id} className="aj-card p-5" data-testid={`ve-card-${e.empresa_id}`}>
            <div className="flex items-center gap-2">
              <Building2 size={17} className="text-aj-orange" />
              <span className="font-display font-bold text-aj-blue-deep">{e.empresa_nome}</span>
              {e.telefone_financeiro && (
                <a href={`https://wa.me/55${String(e.telefone_financeiro).replace(/\D/g, "")}`}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-[#166534] bg-[#DCFCE7] rounded px-2 py-0.5 hover:bg-[#bbf7d0]"
                  title="Financeiro da empresa — dúvidas de pagamento"
                  data-testid={`ve-fin-${e.empresa_id}`}>
                  💬 Financeiro
                </a>
              )}
              <span className="text-[10px] text-aj-text-muted ml-auto">
                vigente desde {new Date(e.vigencia_inicio).toLocaleDateString("pt-BR")}
              </span>
            </div>
            {e.aceite?.status === "pendente" && (
              <div className="mt-3 rounded-lg border border-[#F5C518] bg-[#FFF7E0] p-3" data-testid={`ve-aceite-pendente-${e.empresa_id}`}>
                <div className="text-xs font-bold text-[#92400E] mb-2">
                  Contrato pendente: aceite as condições desta empresa para receber os serviços dela.
                </div>
                <div className="flex gap-2">
                  <button onClick={() => navigate(rotaContrato)}
                    className="px-3 py-1.5 rounded-md bg-[#FF6700] hover:bg-[#e05a00] text-white text-xs font-bold inline-flex items-center gap-1.5"
                    data-testid={`ve-aceitar-${e.empresa_id}`}>
                    <FileSignature size={13} /> Aceitar e assinar contrato
                  </button>
                  <button onClick={() => recusar(e)}
                    className="px-3 py-1.5 rounded-md border border-[#DC2626] text-[#DC2626] hover:bg-red-50 text-xs font-bold"
                    data-testid={`ve-recusar-${e.empresa_id}`}>
                    Recusar
                  </button>
                </div>
              </div>
            )}
            {e.aceite?.status === "aceito" && (
              <div className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#166534]" data-testid={`ve-aceito-${e.empresa_id}`}>
                <CheckCircle2 size={13} /> Contrato aceito — disponível em Contratos Comerciais
              </div>
            )}
            {!e.aceite && !e.condicoes_publicadas && (
              <div className="mt-2 text-[11px] text-aj-text-muted italic" data-testid={`ve-sem-condicoes-${e.empresa_id}`}>
                ⏳ Esta empresa ainda não publicou as condições comerciais completas — o aceite ficará disponível assim que publicar.
              </div>
            )}
            {!e.aceite && e.condicoes_publicadas && (
              <div className="mt-2 text-[11px] text-aj-text-muted italic" data-testid={`ve-fora-elegibilidade-${e.empresa_id}`}>
                Condições publicadas — aceite indisponível para o seu perfil ou região no momento.
              </div>
            )}
            <table className="w-full text-sm mt-3">
              <thead>
                <tr className="border-b border-[#E2E8F0]">
                  <th className="text-left py-2 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Serviço</th>
                  <th className="text-right py-2 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">
                    <Wallet size={11} className="inline mr-1" />Você recebe
                  </th>
                </tr>
              </thead>
              <tbody>
                {(e.itens || []).map((it) => (
                  <tr key={it.tipo_servico} className="border-b border-[#F1F5F9] last:border-0">
                    <td className="py-2">{labels[it.tipo_servico] || (it.tipo_servico || "").replace(/_/g, " ")}</td>
                    <td className="py-2 text-right font-mono font-bold text-aj-blue-deep">{fmtValor(it)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 pt-3 border-t border-[#E2E8F0] text-sm">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-aj-text-muted mb-1">
                <CalendarDays size={12} /> Pagamento
              </div>
              {e.calendario ? (
                <div className="text-aj-text" data-testid={`ve-cal-${e.empresa_id}`}>
                  <span className="capitalize font-semibold">{e.calendario.periodicidade}</span>
                  {" · "}{e.calendario.forma_pagamento || "PIX"}
                  {(e.calendario.periodos || []).length > 0 && (
                    <div className="text-xs text-aj-text-muted mt-1">
                      {(e.calendario.periodos || []).map((p, i) => (
                        <span key={i} className="inline-block bg-aj-surface-2 rounded px-2 py-0.5 mr-1 mb-1">
                          {typeof p === "object" ? Object.values(p).join(" → ") : String(p)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-xs text-aj-text-muted">Calendário de pagamento ainda não informado pela empresa.</span>
              )}
              {e.observacoes && <p className="text-xs text-aj-text-muted mt-2">Obs: {e.observacoes}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
